import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, X, Send, Mic, MicOff, Square, Video, VideoOff, Volume2, VolumeX,
  CheckCircle2, ListChecks, Gavel, CalendarPlus, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { Room, RoomEvent } from "livekit-client";

const SUGGESTIONS = [
  "What's on my plate today?",
  "Add a high-priority task to email the vendor by Friday",
  "Approve the decision about the new budget",
  "Schedule a standup tomorrow at 10am",
];

const ENTITY_ICON = { task: ListChecks, decision: Gavel, meeting: CalendarPlus };

export default function HelpAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi, I'm your Yanthra AI Agent. Talk or type — I can create tasks, approve decisions, schedule meetings, or answer questions." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [avatarState, setAvatarState] = useState("off"); // off | starting | live | unavailable
  const [pendingPlan, setPendingPlan] = useState(null);
  const pendingPlanRef = useRef(null);
  const [micAlwaysOn, setMicAlwaysOn] = useState(true);
  const micAlwaysOnRef = useRef(true);
  useEffect(() => { micAlwaysOnRef.current = micAlwaysOn; }, [micAlwaysOn]);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const chromaRafRef = useRef(null);
  const avatarStateRef = useRef("off");
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const sendingRef = useRef(false);
  const greetedRef = useRef(false);
  // continuous (always-on) listening in avatar mode
  const contRef = useRef({ stream: null, ctx: null, analyser: null, raf: null, rec: null, chunks: [], hadSpeech: false, silenceStart: 0, active: false, running: false });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  useEffect(() => () => { teardownAvatar(); }, []);

  // ---------- HeyGen LiveAvatar (LITE mode): LiveKit video + PCM-over-WebSocket speech ----------
  const roomRef = useRef(null);
  const wsRef = useRef(null);
  const wsReadyRef = useRef(false);
  const keepAliveRef = useRef(null);
  const sessionIdRef = useRef(null);
  const audioElRef = useRef(null);
  const speakClearRef = useRef(null);
  // Synchronous mirrors of busy/speaking so the always-on mic loop never acts on
  // a stale value (the React state lags a tick behind and caused duplicate sends).
  const busyRef = useRef(false);
  const speakingRef = useRef(false);
  const lastHeardRef = useRef({ text: "", at: 0 });
  function setBusyNow(v) { busyRef.current = v; setBusy(v); }

  // Mark the agent as "speaking" and guarantee it clears, even if the avatar's
  // speak-ended event never arrives (HeyGen sometimes drops it). ms is derived
  // from the audio's own length, capped so the mic can never stay blocked.
  function markSpeaking(ms) {
    if (speakClearRef.current) clearTimeout(speakClearRef.current);
    speakingRef.current = true; setSpeaking(true);
    const safe = Math.min(Math.max(ms || 2500, 1200), 30000);
    speakClearRef.current = setTimeout(() => { speakClearRef.current = null; speakingRef.current = false; setSpeaking(false); }, safe);
  }
  function clearSpeaking() {
    if (speakClearRef.current) { clearTimeout(speakClearRef.current); speakClearRef.current = null; }
    speakingRef.current = false; setSpeaking(false);
  }

  function teardownAvatar() {
    try { if (keepAliveRef.current) clearInterval(keepAliveRef.current); } catch (e) { /* ignore */ }
    try { if (speakClearRef.current) clearTimeout(speakClearRef.current); } catch (e) { /* ignore */ }
    speakClearRef.current = null; setSpeaking(false);
    try { if (chromaRafRef.current) cancelAnimationFrame(chromaRafRef.current); } catch (e) { /* ignore */ }
    try { wsRef.current?.close(); } catch (e) { /* ignore */ }
    try { roomRef.current?.disconnect(); } catch (e) { /* ignore */ }
    if (sessionIdRef.current) { api.post("/liveavatar/stop", { session_id: sessionIdRef.current }).catch(() => {}); }
    wsRef.current = null; roomRef.current = null; wsReadyRef.current = false; sessionIdRef.current = null;
  }

  // Chroma-key the avatar's green screen and composite it over a solid branding-blue
  // backdrop drawn straight onto the canvas (guarantees a clean blue background).
  useEffect(() => { avatarStateRef.current = avatarState; }, [avatarState]);
  function startChroma() {
    if (chromaRafRef.current) cancelAnimationFrame(chromaRafRef.current);
    const BR = 13, BG = 50, BB = 122; // branding blue (#0D327A)
    const draw = () => {
      if (avatarStateRef.current !== "live") return;
      const v = videoRef.current, cv = canvasRef.current;
      if (v && cv && v.videoWidth > 0) {
        const W = 480, H = Math.round((W * v.videoHeight) / v.videoWidth);
        if (cv.width !== W) { cv.width = W; cv.height = H; }
        const ctx = cv.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(v, 0, 0, W, H);
        try {
          const img = ctx.getImageData(0, 0, W, H);
          const d = img.data;
          for (let i = 0; i < d.length; i += 4) {
            const r = d[i], g = d[i + 1], b = d[i + 2];
            if (g > 95 && g > r * 1.3 && g > b * 1.3) {
              d[i] = BR; d[i + 1] = BG; d[i + 2] = BB; // green → branding blue
            } else if (g > r && g > b) {
              d[i + 1] = Math.round((r + b) / 2); // suppress green spill on edges
            }
          }
          ctx.putImageData(img, 0, 0);
        } catch (e) { /* ignore a transient read error */ }
      }
      chromaRafRef.current = requestAnimationFrame(draw);
    };
    chromaRafRef.current = requestAnimationFrame(draw);
  }
  useEffect(() => {
    if (avatarState === "live") startChroma();
    else if (chromaRafRef.current) cancelAnimationFrame(chromaRafRef.current);
  }, [avatarState]); // eslint-disable-line react-hooks/exhaustive-deps

  const startAvatar = useCallback(async () => {
    if (avatarState === "live" || avatarState === "starting") return;
    setAvatarState("starting");
    try {
      const { data } = await api.post("/liveavatar/session", {});
      if (!data?.livekit_url || !data?.livekit_client_token) { setAvatarState("unavailable"); return; }
      sessionIdRef.current = data.session_id;

      const room = new Room({ adaptiveStream: true });
      roomRef.current = room;
      const attach = (track) => {
        if (track.kind === "video" && videoRef.current) {
          track.attach(videoRef.current);
          videoRef.current.play().catch(() => {});
        } else if (track.kind === "audio" && audioElRef.current) {
          track.attach(audioElRef.current);
        }
      };
      room.on(RoomEvent.TrackSubscribed, attach);
      room.on(RoomEvent.Disconnected, () => setAvatarState("off"));
      await room.connect(data.livekit_url, data.livekit_client_token);
      setAvatarState("live");
      // Re-attach tracks that subscribed before the video element was ready.
      setTimeout(() => {
        try {
          room.remoteParticipants.forEach((p) => p.trackPublications.forEach((pub) => pub.track && attach(pub.track)));
        } catch (e) { /* ignore */ }
      }, 400);

      // Control WebSocket for agent.speak (PCM audio).
      const ws = new WebSocket(data.ws_url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "session.state_updated" && msg.state === "connected") wsReadyRef.current = true;
          if (msg.type === "agent.speak_started") setSpeaking(true);
          if (msg.type === "agent.speak_ended" || msg.type === "agent.speak_interrupted") clearSpeaking();
        } catch (e) { /* ignore */ }
      };
      ws.onclose = () => { wsReadyRef.current = false; };
      keepAliveRef.current = setInterval(() => {
        try { wsRef.current?.readyState === 1 && wsRef.current.send(JSON.stringify({ type: "session.keep_alive" })); } catch (e) { /* ignore */ }
      }, 60000);
    } catch (e) { setAvatarState("unavailable"); teardownAvatar(); }
  }, [avatarState]);

  function stopAvatar() {
    teardownAvatar();
    if (videoRef.current) videoRef.current.srcObject = null;
    setAvatarState("off");
  }

  async function speakViaAvatar(text) {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1 || !wsReadyRef.current) return false;
    const { data } = await api.post("/assistant/tts", { text: text.slice(0, 1500), format: "pcm" });
    if (!data?.audio_base64) return false;
    const eid = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    const b64 = data.audio_base64;
    const MAX = 700000; // keep each WS frame < 1MB
    for (let i = 0; i < b64.length; i += MAX) {
      ws.send(JSON.stringify({ type: "agent.speak", event_id: eid, audio: b64.slice(i, i + MAX) }));
    }
    ws.send(JSON.stringify({ type: "agent.speak_end", event_id: eid }));
    // Estimate playback length from the PCM payload (16-bit, 24kHz mono) so the
    // "speaking" state always clears even if HeyGen never sends speak_ended.
    const bytes = Math.floor((b64.length * 3) / 4);
    const durMs = (bytes / 2 / 24000) * 1000;
    markSpeaking(durMs + 1800);
    return true;
  }

  // ---------- Spoken replies ----------
  const speakReply = useCallback(async (text) => {
    if (!voiceOn || !text) return;
    if (avatarState === "live") {
      try { if (await speakViaAvatar(text)) return; } catch (e) { /* fall through to TTS */ }
    }
    try {
      const { data } = await api.post("/assistant/tts", { text: text.slice(0, 1200) });
      if (!data?.audio_base64) return;
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => clearSpeaking();
      audio.onerror = () => clearSpeaking();
      audio.onloadedmetadata = () => { if (isFinite(audio.duration) && audio.duration > 0) markSpeaking(audio.duration * 1000 + 800); };
      await audio.play();
    } catch (e) { setSpeaking(false); }
  }, [voiceOn, avatarState]);

  // Greet out loud the first time the panel is opened (the open-click is the
  // user gesture that lets the browser play audio) — fixes "no voice on open".
  useEffect(() => {
    if (open && !greetedRef.current && voiceOn) {
      greetedRef.current = true;
      speakReply("Hi, I'm your Yanthra AI Agent. How can I help?");
    }
  }, [open, voiceOn, speakReply]);

  // ---------- Execute a user-confirmed plan ----------
  const executePlan = useCallback(async (plan) => {
    setBusyNow(true);
    try {
      const { data } = await api.post("/assistant/command", { plan });
      const reply = data?.reply || "Done.";
      setMessages((m) => [...m, { role: "bot", text: reply, result: data?.result, intent: data?.intent }]);
      speakReply(reply);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: "I couldn't complete that — please try again." }]);
    } finally {
      setBusyNow(false);
    }
  }, [speakReply]);

  // ---------- Send a command (text or transcribed voice) ----------
  const sendCommand = useCallback(async (text) => {
    const cmd = (text ?? "").trim();
    if (!cmd || sendingRef.current) return;
    sendingRef.current = true;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: cmd }]);
    try {
      // If a confirmation is pending, interpret yes / no locally first.
      if (pendingPlanRef.current) {
        const low = cmd.toLowerCase();
        if (/^(y\b|yes|yeah|yep|confirm|go ahead|do it|sure|ok|okay|please do|correct)/.test(low)) {
          const plan = pendingPlanRef.current;
          pendingPlanRef.current = null; setPendingPlan(null);
          await executePlan(plan);
          return;
        }
        if (/^(n\b|no|nope|cancel|stop|never ?mind|don'?t|forget)/.test(low)) {
          pendingPlanRef.current = null; setPendingPlan(null);
          const r = "Okay, cancelled — nothing was saved.";
          setMessages((m) => [...m, { role: "bot", text: r }]);
          speakReply(r);
          return;
        }
        // Anything else => treat as a brand-new command.
        pendingPlanRef.current = null; setPendingPlan(null);
      }

      setBusyNow(true);
      const { data } = await api.post("/assistant/command", { text: cmd });
      const reply = data?.reply || "Done.";
      if (data?.needs_confirmation && data?.plan) {
        pendingPlanRef.current = data.plan; setPendingPlan(data.plan);
        setMessages((m) => [...m, { role: "bot", text: reply, confirm: true }]);
      } else {
        setMessages((m) => [...m, { role: "bot", text: reply, result: data?.result, intent: data?.intent }]);
      }
      speakReply(reply);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: "I'm having trouble right now — please try again in a moment." }]);
    } finally {
      sendingRef.current = false;
      setBusyNow(false);
    }
  }, [speakReply, executePlan]);

  function confirmPending() {
    const plan = pendingPlanRef.current;
    if (!plan) return;
    pendingPlanRef.current = null; setPendingPlan(null);
    setMessages((m) => [...m, { role: "user", text: "Yes, go ahead" }]);
    executePlan(plan);
  }
  function cancelPending() {
    pendingPlanRef.current = null; setPendingPlan(null);
    const r = "Okay, cancelled — nothing was saved.";
    setMessages((m) => [...m, { role: "bot", text: r }]);
    speakReply(r);
  }

  // ---------- Microphone (record -> STT -> command) ----------
  async function startListening() {
    if (listening || busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 800) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "command.webm");
          const { data } = await api.post("/assistant/stt", fd);
          const heard = (data?.text || "").trim();
          setBusy(false);
          if (heard) sendCommand(heard);
          else setMessages((m) => [...m, { role: "bot", text: "I didn't catch that — could you try again?" }]);
        } catch (e) {
          setBusy(false);
          setMessages((m) => [...m, { role: "bot", text: "I couldn't process the audio. Please check your microphone and try again." }]);
        }
      };
      rec.start();
      setListening(true);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: "I need microphone access to hear you. Please allow it in your browser, or type your command." }]);
    }
  }

  function stopListening() {
    if (!listening) return;
    setListening(false);
    try { recorderRef.current?.stop(); } catch (e) { /* ignore */ }
  }

  // ---------- Always-on listening (avatar mode): mic VAD loop ----------
  const sendCommandRef = useRef(() => {});
  useEffect(() => { busyRef.current = busy; }, [busy]);
  useEffect(() => { speakingRef.current = speaking; }, [speaking]);
  useEffect(() => { sendCommandRef.current = sendCommand; }, [sendCommand]);

  // Live mode = always listening, speak-naturally. Every utterance is a command
  // (no wake word). We only drop an exact repeat heard within 6s (STT can emit
  // the same phrase twice from a trailing segment), and never act on our own voice.
  const handleHandsFree = useCallback((heard) => {
    const text = (heard || "").trim();
    if (!text) return;
    const now = Date.now();
    const last = lastHeardRef.current;
    if (text.toLowerCase() === (last.text || "").toLowerCase() && now - last.at < 6000) return;
    lastHeardRef.current = { text, at: now };
    sendCommandRef.current(text);
  }, []);
  const handleHandsFreeRef = useRef(handleHandsFree);
  useEffect(() => { handleHandsFreeRef.current = handleHandsFree; }, [handleHandsFree]);

  function startContinuous() {
    const c = contRef.current;
    if (c.running) return;
    navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
      .then((stream) => {
        c.stream = stream; c.running = true;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        c.ctx = ctx;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(analyser);
        c.analyser = analyser;
        const buf = new Uint8Array(analyser.fftSize);
        const START = 0.05, SILENCE = 0.03, SIL_MS = 1100;
        const tick = () => {
          if (!c.running) return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
          const rms = Math.sqrt(sum / buf.length);
          const idle = !busyRef.current && !speakingRef.current;
          if (!c.rec) {
            if (idle && micAlwaysOnRef.current && rms > START) startSegment();
          } else if (speakingRef.current) {
            abortSegment();
          } else if (rms > SILENCE) {
            c.hadSpeech = true; c.silenceStart = 0;
          } else if (!c.silenceStart) {
            c.silenceStart = performance.now();
          } else if (performance.now() - c.silenceStart > SIL_MS) {
            stopSegment();
          }
          c.raf = requestAnimationFrame(tick);
        };
        c.raf = requestAnimationFrame(tick);
      })
      .catch(() => {
        setMicAlwaysOn(false);
        setMessages((m) => [...m, { role: "bot", text: "I need microphone access to listen hands-free. Please allow it, or type your command." }]);
      });
  }

  function startSegment() {
    const c = contRef.current;
    try {
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(c.stream, mime ? { mimeType: mime } : undefined);
      c.rec = rec; c.chunks = []; c.hadSpeech = false; c.silenceStart = 0;
      rec.ondataavailable = (ev) => { if (ev.data.size > 0) c.chunks.push(ev.data); };
      rec.onstop = async () => {
        const had = c.hadSpeech;
        const blob = new Blob(c.chunks, { type: "audio/webm" });
        c.rec = null; setListening(false);
        if (!had || blob.size < 1500) return;
        setBusyNow(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "command.webm");
          const { data } = await api.post("/assistant/stt", fd);
          const heard = (data?.text || "").trim();
          setBusyNow(false);
          if (heard) handleHandsFreeRef.current(heard);
        } catch (e) { setBusyNow(false); }
      };
      rec.start(); setListening(true);
    } catch (e) { c.rec = null; }
  }
  function stopSegment() { try { contRef.current.rec?.stop(); } catch (e) { /* ignore */ } }
  function abortSegment() { const c = contRef.current; c.hadSpeech = false; try { c.rec?.stop(); } catch (e) { /* ignore */ } c.rec = null; setListening(false); }

  function stopContinuous() {
    const c = contRef.current;
    if (!c.running && !c.stream) return;
    c.running = false;
    try { if (c.raf) cancelAnimationFrame(c.raf); } catch (e) { /* ignore */ }
    try { c.rec?.stop(); } catch (e) { /* ignore */ }
    try { c.stream?.getTracks?.().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
    try { c.ctx?.close(); } catch (e) { /* ignore */ }
    contRef.current = { stream: null, ctx: null, analyser: null, raf: null, rec: null, chunks: [], hadSpeech: false, silenceStart: 0, active: false, running: false };
    setListening(false);
  }

  useEffect(() => {
    if (avatarState === "live" && micAlwaysOn) startContinuous();
    else stopContinuous();
  }, [avatarState, micAlwaysOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const orbActive = speaking || listening || busy;

  return (
    <>
      {!open && (
        <button
          id="meetiq-help-btn"
          data-testid="help-launcher-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full px-5 py-3 text-white font-semibold shadow-xl transition-transform hover:scale-[1.03]"
          style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}
        >
          <Sparkles className="w-4 h-4" strokeWidth={2} /> Ask Yanthra AI
        </button>
      )}

      {open && (
        <div
          data-testid="help-panel"
          className="fixed bottom-5 right-5 z-50 w-[400px] max-w-[94vw] rounded-3xl bg-card border border-border shadow-2xl overflow-hidden flex flex-col"
          style={{ height: "640px", animation: "mi-pop .18s ease-out" }}
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--primary-soft)))" }}>
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4" strokeWidth={1.8} />
              <div className="leading-none">
                <div className="text-sm font-semibold">Yanthra AI Agent</div>
                <div className="text-[10px] opacity-80 mt-0.5">voice &amp; actions · by Yanthra Labs</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="help-voice-toggle" onClick={() => setVoiceOn((v) => !v)} title={voiceOn ? "Mute replies" : "Unmute replies"}
                className="p-1.5 rounded-full text-white/90 hover:bg-white/15 transition-colors">
                {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button data-testid="help-avatar-toggle" onClick={avatarState === "live" ? stopAvatar : startAvatar} title="Live avatar"
                className="p-1.5 rounded-full text-white/90 hover:bg-white/15 transition-colors">
                {avatarState === "live" ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
              </button>
              <button data-testid="help-close-btn" onClick={() => setOpen(false)} className="p-1.5 rounded-full text-white/90 hover:bg-white/15 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Avatar / Voice-orb stage */}
          <div data-testid="assistant-stage" className="relative grid place-items-center overflow-hidden"
            style={{ height: "188px", background: "linear-gradient(140deg, #0a1f4d 0%, #0d327a 55%, #1546a8 100%)" }}>
            {/* subtle studio glow behind the avatar */}
            {avatarState === "live" && (
              <div className="absolute inset-0" style={{ background: "radial-gradient(60% 75% at 50% 42%, rgba(80,150,255,0.25) 0%, rgba(10,31,77,0) 70%)" }} />
            )}
            {/* hidden source video (green screen); we chroma-key it onto the canvas */}
            <video ref={videoRef} autoPlay playsInline muted className="absolute w-px h-px opacity-0 pointer-events-none" />
            <canvas data-testid="assistant-avatar-video" ref={canvasRef}
              className={avatarState === "live" ? "relative w-full h-full object-contain" : "hidden"} />
            {avatarState !== "live" && (
              <VoiceOrb active={orbActive} listening={listening} speaking={speaking} />
            )}
            <audio ref={audioElRef} autoPlay />
            {avatarState === "starting" && (
              <div className="absolute inset-0 grid place-items-center text-white text-xs bg-black/40">Starting live avatar…</div>
            )}
            <div data-testid="assistant-status" className="absolute bottom-2 left-0 right-0 text-center text-[11px] text-white/80 tracking-wide">
              {busy ? "Thinking…"
                : speaking ? "Speaking…"
                : (avatarState === "live" && micAlwaysOn)
                  ? (listening ? "Listening…" : "Listening — just speak")
                  : (avatarState === "live" && !micAlwaysOn) ? "Mic paused — tap the mic to resume"
                  : listening ? "Listening…"
                  : avatarState === "unavailable" ? "Voice mode (live avatar unavailable on this plan)"
                  : "Tap the mic and speak, or type below"}
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-secondary/30">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={m.role === "user"
                  ? "max-w-[82%] rounded-2xl rounded-br-sm px-3 py-2 text-sm text-white"
                  : "max-w-[88%] rounded-2xl rounded-bl-sm px-3 py-2 text-sm bg-card border border-border"}
                  style={m.role === "user" ? { background: "hsl(var(--primary))" } : {}}>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  {m.role === "bot" && m.result?.ok && <ActionChip result={m.result} />}
                  {m.role === "bot" && m.confirm && pendingPlan && i === messages.length - 1 && (
                    <div data-testid="assistant-confirm-actions" className="mt-2 flex items-center gap-2">
                      <button data-testid="assistant-confirm-yes" onClick={confirmPending}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1 text-[11px] font-semibold hover:bg-emerald-700 transition-colors">
                        <CheckCircle2 className="w-3 h-3" /> Yes, do it
                      </button>
                      <button data-testid="assistant-confirm-no" onClick={cancelPending}
                        className="inline-flex items-center gap-1 rounded-full bg-secondary text-foreground px-3 py-1 text-[11px] font-semibold hover:bg-secondary/70 transition-colors">
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Working…</div>}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid={`help-suggestion-${s.slice(0, 10).replace(/\s+/g, "-").toLowerCase()}`} onClick={() => sendCommand(s)}
                    className="tone-powder rounded-full px-3 py-1 text-[11px] font-medium hover:opacity-80 transition-opacity text-left">{s}</button>
                ))}
              </div>
            )}
          </div>

          {/* Input + mic */}
          <form data-testid="help-form" onSubmit={(e) => { e.preventDefault(); sendCommand(input); }}
            className="p-3 border-t border-border flex items-center gap-2 bg-card">
            <button type="button" data-testid="help-mic-btn"
              onClick={avatarState === "live" ? () => setMicAlwaysOn((v) => !v) : (listening ? stopListening : startListening)}
              title={avatarState === "live" ? (micAlwaysOn ? "Always listening — tap to pause" : "Mic paused — tap to resume") : (listening ? "Stop" : "Speak")}
              className={`shrink-0 w-10 h-10 grid place-items-center rounded-full text-white transition-transform ${listening ? "scale-110" : ""}`}
              style={{ background: (avatarState === "live" && !micAlwaysOn) ? "#94a3b8" : (listening ? "#e11d48" : "hsl(var(--navy))") }}>
              {avatarState === "live" && !micAlwaysOn ? <MicOff className="w-4 h-4" />
                : listening ? <Square className="w-4 h-4" fill="white" /> : <Mic className="w-4 h-4" />}
            </button>
            <input
              data-testid="help-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask or command Yanthra AI…"
              className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button data-testid="help-send-btn" type="submit" disabled={busy || !input.trim()}
              className="shrink-0 w-10 h-10 grid place-items-center rounded-full text-white disabled:opacity-50 transition-opacity"
              style={{ background: "hsl(var(--cyan))" }}>
              <Send className="w-4 h-4" strokeWidth={2} />
            </button>
          </form>
        </div>
      )}

      <style>{`
        @keyframes mi-pop { from { opacity:0; transform: translateY(10px) scale(.98) } to { opacity:1; transform:none } }
        @keyframes mi-orb { 0%,100% { transform: scale(1) } 50% { transform: scale(1.08) } }
        @keyframes mi-ring { 0% { transform: scale(.7); opacity:.55 } 100% { transform: scale(1.8); opacity:0 } }
      `}</style>
    </>
  );
}

function VoiceOrb({ active, listening, speaking }) {
  const hue = listening ? "#e11d48, #fb7185" : speaking ? "#29abe2, #67e8f9" : "#29abe2, #6c5fbc";
  return (
    <div data-testid="assistant-voice-orb" className="relative grid place-items-center" style={{ width: 130, height: 130 }}>
      {active && [0, 1, 2].map((r) => (
        <span key={r} className="absolute rounded-full"
          style={{ width: 96, height: 96, border: `2px solid ${listening ? "#fb7185" : "#67e8f9"}`,
            animation: `mi-ring 1.6s ease-out ${r * 0.5}s infinite` }} />
      ))}
      <div className="rounded-full shadow-2xl"
        style={{
          width: 92, height: 92,
          background: `radial-gradient(circle at 32% 28%, ${hue})`,
          boxShadow: `0 0 42px ${listening ? "rgba(251,113,133,.7)" : "rgba(41,171,226,.65)"}`,
          animation: active ? "mi-orb 1.4s ease-in-out infinite" : "none",
        }} />
      <Sparkles className="absolute w-7 h-7 text-white/90" strokeWidth={1.6} />
    </div>
  );
}

function ActionChip({ result }) {
  const Icon = ENTITY_ICON[result.entity] || CheckCircle2;
  return (
    <Link to={result.link || "#"} data-testid="assistant-action-chip"
      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors">
      <Icon className="w-3 h-3" />
      {result.entity === "task" ? "View task" : result.entity === "decision" ? "View decision" : result.entity === "meeting" ? "Open meeting" : "View"}
    </Link>
  );
}
